import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { site, trustItems, homeMaterials, services, processSteps, whyUs, galleryProjects, IMG } from "../data/site";
import { Seo, businessLd, websiteLd, organizationLd } from "../components/Seo";
import { Eyebrow, SectionHeading, CTABand } from "../components/ui-blocks";

const HOME_JSONLD = [businessLd, websiteLd, organizationLd];

const Home = () => {
  const featured = galleryProjects.slice(0, 6);

  return (
    <div>
      <Seo
        title="Granite & Quartz Countertops"
        description="Premier Granite LLC provides professional granite, quartz and stone fabrication and installation for residential and commercial projects. Request a free estimate."
        path="/"
        jsonLd={HOME_JSONLD}
      />

      {/* 1. HERO */}
      <section className="relative min-h-[92vh] flex flex-col items-start" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={IMG.heroKitchen} alt="Custom stone kitchen with island" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        </div>
        {/* Logo on the front page (hero) */}
        <div className="container-custom relative z-10 pt-8 w-full">
          <img src={site.logoLight} alt="Premier Granite LLC" className="h-20 md:h-28 w-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.25)]" data-testid="hero-logo" />
        </div>
        <div className="container-custom relative z-10 mt-auto pb-20 md:pb-24 w-full">
          <div className="max-w-2xl animate-fade-up hero-text">
            <div className="mb-6"><Eyebrow light>Stone Fabrication & Installation</Eyebrow></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-3">
              Craftsmanship Built to Last.
            </h1>
            <p className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#CD1422] mb-7">
              Excellence Set in Stone.
            </p>
            <p className="text-white/80 text-base md:text-lg leading-relaxed mb-9 max-w-xl">
              Custom stone fabrication and installation for kitchens, bathrooms, commercial spaces and more.
              Built with precision. Installed with care.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-estimate" className="btn btn-primary">
                Get a Free Estimate <ArrowRight size={18} />
              </Link>
              <Link to="/gallery" data-testid="hero-view-work" className="btn btn-outline-light">View Our Work</Link>
            </div>
            <Link to="/materials" className="inline-flex items-center gap-2 mt-6 text-white/70 hover:text-white text-sm transition-colors">
              Explore Materials <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* 2. TRUST STRIP */}
      <section className="bg-[#111827] py-6" data-testid="trust-strip">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-3">
            {trustItems.map((t) => (
              <div key={t} className="flex items-center gap-2 text-white/85 text-sm font-medium tracking-wide">
                <span className="w-1.5 h-1.5 bg-[#CD1422] rounded-full" />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. INTRODUCTION */}
      <section className="section bg-white">
        <div className="container-custom grid lg:grid-cols-2 gap-14 items-center">
          <div className="img-zoom rounded-lg overflow-hidden order-2 lg:order-1">
            <img src={IMG.kitchenDining} alt="Open-concept kitchen with stone surfaces" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="order-1 lg:order-2">
            <SectionHeading
              eyebrow="Premier Granite LLC"
              title="Stonework Designed Around Your Space"
              copy="At Premier Granite LLC, every project begins with the space, the material and the vision behind it. From kitchen countertops and islands to bathroom vanities and commercial surfaces, our goal is simple: deliver beautiful stonework with precise fabrication, careful installation and lasting craftsmanship."
            />
            <Link to="/about" className="btn btn-secondary mt-8">About Premier Granite</Link>
          </div>
        </div>
      </section>

      {/* 4. MATERIALS */}
      <section className="section bg-[#FAFAF9] border-y border-neutral-200" data-testid="home-materials">
        <div className="container-custom">
          <SectionHeading center eyebrow="Materials" title="Surfaces Chosen for Beauty & Performance" className="mb-14" />
          <div className="grid md:grid-cols-3 gap-8">
            {homeMaterials.map((m) => (
              <Link key={m.slug} to={`/materials/${m.slug}`} className="card-flat group" data-testid={`material-card-${m.slug}`}>
                <div className="img-zoom h-60 overflow-hidden">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl mb-2">{m.name}</h3>
                  <p className="text-neutral-600 text-sm mb-4">{m.blurb}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-[#CD1422]">
                    Learn more <ArrowRight size={15} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/materials" className="btn btn-secondary">Explore Materials</Link>
          </div>
        </div>
      </section>

      {/* 5. SERVICES */}
      <section className="section bg-white" data-testid="home-services">
        <div className="container-custom">
          <SectionHeading center eyebrow="Services" title="From Slab to Finished Space" className="mb-14" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((s) => (
              <div key={s.slug} className="card-flat group" data-testid={`service-card-${s.slug}`}>
                <div className="img-zoom h-52 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl mb-2">{s.title}</h3>
                  <p className="text-neutral-600 text-sm">{s.blurb}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="btn btn-secondary">View Services</Link>
          </div>
        </div>
      </section>

      {/* 6. FEATURED PROJECTS */}
      <section className="section bg-[#111827]" data-testid="home-featured">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="flex justify-center mb-4"><Eyebrow light>Crafted Spaces</Eyebrow></div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white mb-4">See the Craftsmanship</h2>
            <p className="text-white/70">A look at the styles, materials and finishes we bring to residential and commercial projects.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((p) => (
              <Link key={p.id} to="/gallery" className="img-zoom relative rounded-lg overflow-hidden group aspect-[4/3]">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <div>
                    <p className="text-white font-medium text-sm">{p.title}</p>
                    <p className="text-white/60 text-xs">{p.material}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/gallery" className="btn btn-outline-light">View Full Gallery</Link>
          </div>
        </div>
      </section>

      {/* 7. PROCESS */}
      <section className="section bg-white" data-testid="home-process">
        <div className="container-custom">
          <SectionHeading center eyebrow="Our Process" title="From Vision to Installation" className="mb-14" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {processSteps.map((step) => (
              <div key={step.step} className="relative">
                <span className="font-display text-5xl text-[#CD1422]/20 font-semibold">{step.step}</span>
                <h3 className="text-lg mt-1 mb-2">{step.title}</h3>
                <p className="text-neutral-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/contact" className="btn btn-primary">Start Your Project <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      {/* 8. RESIDENTIAL + COMMERCIAL */}
      <section className="grid md:grid-cols-2" data-testid="home-split">
        <div className="relative min-h-[420px] flex items-end p-10 lg:p-14">
          <img src={IMG.kitchenIsland} alt="Residential stonework" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A3D84]/95 via-[#1A3D84]/60 to-transparent" />
          <div className="relative z-10 max-w-md">
            <h3 className="text-3xl text-white mb-3">Residential</h3>
            <p className="text-white/80 text-sm mb-6">Kitchen remodels, new construction, bathrooms, islands and specialty stone applications.</p>
            <Link to="/services" className="btn btn-white">Residential Services</Link>
          </div>
        </div>
        <div className="relative min-h-[420px] flex items-end p-10 lg:p-14">
          <img src={IMG.commercialLobby} alt="Commercial stonework" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/95 via-[#111827]/60 to-transparent" />
          <div className="relative z-10 max-w-md">
            <h3 className="text-3xl text-white mb-3">Commercial</h3>
            <p className="text-white/80 text-sm mb-6">Professional stone solutions for qualifying contractors, builders, property owners and commercial projects.</p>
            <Link to="/commercial" className="btn btn-white">Commercial Services</Link>
          </div>
        </div>
      </section>

      {/* 9. WHY PREMIER GRANITE */}
      <section className="section bg-[#FAFAF9] border-y border-neutral-200" data-testid="home-why">
        <div className="container-custom">
          <SectionHeading center eyebrow="Why Us" title="Why Premier Granite?" className="mb-14" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyUs.map((w) => (
              <div key={w.title} className="text-center">
                <div className="w-12 h-12 mx-auto mb-5 rounded-full bg-[#1A3D84] flex items-center justify-center">
                  <Check size={20} className="text-white" />
                </div>
                <h3 className="text-xl mb-2">{w.title}</h3>
                <p className="text-neutral-600 text-sm">{w.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11. FINAL CTA */}
      <CTABand />
    </div>
  );
};

export default Home;
